import { useState, type CSSProperties } from "react";
import {
  Activity as ActivityIcon,
  Bot,
  CalendarDays,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  File,
  FileQuestion,
  FileText,
  ListChecks,
  MessageCircle,
  MoreVertical,
  Plus,
  ReceiptText,
  Scale,
  Sparkles,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DashboardNeedsCard } from "../components/DashboardNeedsCard";
import { DashboardPrimaryGrid } from "../components/DashboardPrimaryGrid";
import { FormActions, Modal } from "../components/Modal";
import { StatusPill, Surface } from "../components/Surface";
import { useWorkspace } from "../data/WorkspaceProvider";
import {
  activePhasesOnDate,
  buildCalendarMonth,
  calendarSelectionDetail,
  formatPhaseDateRange,
  phaseShowsLabel,
  type CalendarSelection,
} from "../lib/dashboard";
import {
  formatBytes,
  formatDate,
  formatLongDate,
  initials,
} from "../lib/format";
import { calculateCommercialSummary, formatMoney } from "../lib/quotes";
import { nextMeeting } from "../lib/meetings";
import { priorityLabel, workItemStatusLabel } from "../lib/work-items";
import type {
  ChangeRequest,
  ChangeRequestStatus,
  Decision,
  DecisionStatus,
  Meeting,
  WorkItem,
  WorkItemPriority,
  WorkItemStatus,
} from "../types";

const boardStatuses: WorkItemStatus[] = [
  "IN_PROGRESS",
  "TO_VALIDATE",
  "VALIDATED",
];
const columnTone: Record<string, string> = {
  IN_PROGRESS: "blue",
  TO_VALIDATE: "violet",
  VALIDATED: "green",
};
const phaseLabels = {
  PENDING: "Pendiente",
  IN_PROGRESS: "En curso",
  COMPLETED: "Completado",
} as const;
const changeLabels: Record<ChangeRequestStatus, string> = {
  NEW: "Nuevo",
  IN_REVIEW: "En revisión",
  ACCEPTED: "Aceptado",
  REJECTED: "Rechazado",
  DEFERRED: "Diferido",
  IN_DEVELOPMENT: "En desarrollo",
  IMPLEMENTED: "Implementado",
};
const decisionLabels: Record<DecisionStatus, string> = {
  APPROVED: "Aprobado",
  REJECTED: "Rechazado",
  DEFERRED: "Diferido",
  PARTIALLY_APPROVED: "Aprobado parcialmente",
};

export function CalendarWidget({ initialMonth }: { initialMonth?: Date }) {
  const { data } = useWorkspace();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<CalendarSelection | null>(null);
  const today = initialMonth ?? new Date();
  const timeZone = data?.meetings.find((meeting) => meeting.timezone)?.timezone ?? "America/Mexico_City";
  const currentParts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(new Date())
      .map((part) => [part.type, part.value]),
  );
  const todayKey = `${currentParts.year}-${currentParts.month}-${currentParts.day}`;
  const shown = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const cells = buildCalendarMonth(shown);
  const phases = data?.phases.filter((phase) => !phase.archived_at) ?? [];
  const milestones =
    data?.milestones.filter((milestone) => !milestone.archived_at) ?? [];
  const meetings = data?.meetings ?? [];
  const detail = selected ? calendarSelectionDetail(selected) : null;
  const select = (next: CalendarSelection) =>
    setSelected((current) =>
      current?.kind === next.kind && current.item.id === next.item.id
        ? null
        : next,
    );
  const label = new Intl.DateTimeFormat("es-MX", {
    month: "long",
    year: "numeric",
  })
    .format(shown)
    .replace(/^./, (value) => value.toUpperCase());
  return (
    <div className="calendar-v1">
      <div className="calendar-nav">
        <button onClick={() => setOffset(offset - 1)} aria-label="Mes anterior">
          ‹
        </button>
        <strong>{label}</strong>
        <button
          onClick={() => setOffset(offset + 1)}
          aria-label="Mes siguiente"
        >
          ›
        </button>
      </div>
      <div className="weekdays">
        {["L", "M", "X", "J", "V", "S", "D"].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="calendar-month-v1">
        {cells.map((cell) => {
          const activePhases = activePhasesOnDate(phases, cell.key);
          const dayMilestones = milestones.filter(
            (item) => item.date.slice(0, 10) === cell.key,
          );
          const dayMeetings = meetings.filter(
            (item) =>
              (item.starts_at?.slice(0, 10) ?? item.date_window_start) ===
              cell.key,
          );
          return (
            <div
              className={`calendar-day-v1 ${cell.muted ? "muted" : ""} ${cell.key === todayKey ? "today" : ""}`}
              key={cell.key}
            >
              <time>{cell.date.getDate()}</time>
              {activePhases.map((phase) => {
                const showLabel = phaseShowsLabel(phase, cell);
                const isSelected =
                  selected?.kind === "phase" && selected.item.id === phase.id;
                return (
                  <button
                    type="button"
                    key={phase.id}
                    title={phase.name}
                    aria-label={`Ver fase ${phase.name}, ${formatPhaseDateRange(phase.start_date, phase.end_date)}`}
                    aria-pressed={isSelected}
                    className={`phase-span ${phase.status.toLowerCase()} ${phase.start_date === cell.key ? "start" : ""} ${phase.end_date === cell.key ? "end" : ""} ${isSelected ? "selected" : ""}`}
                    style={{ backgroundColor: phase.calendar_color || "#1467E9", color: "#fff" }}
                    onClick={() => select({ kind: "phase", item: phase })}
                  >
                    <span className="phase-span-label">
                      {showLabel ? phase.name : ""}
                    </span>
                  </button>
                );
              })}
              {dayMilestones.map((item) => (
                <button
                  type="button"
                  className={`calendar-chip milestone ${selected?.kind === "milestone" && selected.item.id === item.id ? "selected" : ""}`}
                  title={item.name}
                  aria-label={`Ver hito ${item.name}`}
                  aria-pressed={
                    selected?.kind === "milestone" && selected.item.id === item.id
                  }
                  onClick={() => select({ kind: "milestone", item })}
                  key={item.id}
                >
                  <span aria-hidden="true">◆</span> {item.name}
                </button>
              ))}
              {dayMeetings.map((item) => (
                <button
                  type="button"
                  className={`calendar-chip meeting ${selected?.kind === "meeting" && selected.item.id === item.id ? "selected" : ""}`}
                  title={item.title}
                  aria-label={`Ver reunión ${item.title}`}
                  aria-pressed={
                    selected?.kind === "meeting" && selected.item.id === item.id
                  }
                  onClick={() => select({ kind: "meeting", item })}
                  key={item.id}
                >
                  <span aria-hidden="true">●</span> {item.title}
                </button>
              ))}
            </div>
          );
        })}
      </div>
      {detail && (
        <aside
          className="calendar-detail-panel"
          aria-label={`Detalle de ${detail.title}`}
        >
          <header>
            <span className={`calendar-detail-kind ${selected?.kind}`}>
              {detail.kindLabel}
            </span>
            <button type="button" onClick={() => setSelected(null)} aria-label="Cerrar detalle">
              ×
            </button>
          </header>
          <h3>{detail.title}</h3>
          <time>{detail.date}</time>
          {detail.time && <p className="calendar-detail-time">{detail.time}</p>}
          {detail.status && (
            <StatusPill
              tone={
                selected?.kind === "phase" && selected.item.status === "COMPLETED"
                  ? "green"
                  : selected?.kind === "phase" && selected.item.status === "IN_PROGRESS"
                    ? "blue"
                    : "gray"
              }
            >
              {detail.status}
            </StatusPill>
          )}
          {detail.description && (
            <div className="calendar-detail-description">
              <strong>Descripción</strong>
              <p>{detail.description}</p>
            </div>
          )}
          {detail.link && (
            <a href={detail.link} target="_blank" rel="noreferrer">
              Abrir enlace de la reunión
            </a>
          )}
        </aside>
      )}
    </div>
  );
}

function ProjectStatus() {
  const { data } = useWorkspace();
  const phases =
    data?.phases
      .filter((phase) => !phase.archived_at)
      .sort((a, b) => a.position - b.position) ?? [];
  const current = phases.find((phase) => phase.status === "IN_PROGRESS");
  return (
    <Surface
      title="Estado / Roadmap"
      icon={<ActivityIcon size={18} />}
      className="project-status dashboard-card"
    >
      <div className="status-top">
        <span>Avance general</span>
        <StatusPill tone={current ? "blue" : "gray"}>
          {current ? `Fase actual · ${current.name}` : data?.project.status}
        </StatusPill>
      </div>
      <div
        className="phase-track"
        style={{ "--phase-count": Math.max(phases.length, 1) } as CSSProperties}
      >
        {phases.map((phase, index) => (
          <div
            className={`phase ${phase.status.toLowerCase().replace("_", "-")}`}
            key={phase.id}
          >
            <div className="phase-line">
              <span>
                {phase.status === "COMPLETED" ? (
                  <Check size={16} />
                ) : phase.status === "IN_PROGRESS" ? (
                  <Clock3 size={16} />
                ) : (
                  index + 1
                )}
              </span>
            </div>
            <strong title={phase.name}>{phase.name}</strong>
            <small>
              {formatPhaseDateRange(phase.start_date, phase.end_date)}
            </small>
            <b>{phaseLabels[phase.status]}</b>
          </div>
        ))}
      </div>
    </Surface>
  );
}

function WorkBoard({ onItem }: { onItem(item: WorkItem): void }) {
  const { data, moveWorkItem } = useWorkspace();
  if (!data) return null;
  const owner = data.viewer.role === "POSITRONICO_OWNER";
  return (
    <Surface
      title="Pizarra del proyecto"
      icon={<ListChecks size={18} />}
      className="task-board dashboard-card"
    >
      <div className="board-columns board-columns-v1">
        {boardStatuses.map((status) => {
          const items = data.workItems.filter(
            (item) => item.status === status && !item.archived_at,
          );
          return (
            <section
              className={`board-column ${columnTone[status]}`}
              key={status}
            >
              <header>
                <strong>
                  {workItemStatusLabel(status, data.organization.name)}
                </strong>
                <span>{items.length}</span>
              </header>
              <div className="task-list">
                {items.map((item) => (
                  <article className="task-card" key={item.id}>
                    <button
                      className="task-main"
                      title={item.title}
                      onClick={() => onItem(item)}
                    >
                      <Circle size={19} />
                      <span>{item.title}</span>
                    </button>
                    {owner && (
                      <button
                        className="more-button"
                        aria-label={`Editar ${item.title}`}
                        onClick={() => onItem(item)}
                      >
                        <MoreVertical size={17} />
                      </button>
                    )}
                    <footer>
                      {item.assignee_name && (
                        <span
                          className="mini-avatar"
                          title={item.assignee_name}
                        >
                          {initials(item.assignee_name)}
                        </span>
                      )}
                      <span
                        className={`priority ${item.priority.toLowerCase()}`}
                      >
                        {priorityLabel(item.priority)}
                      </span>
                      {item.comment_count > 0 && (
                        <span>
                          <MessageCircle size={13} />
                          {item.comment_count}
                        </span>
                      )}
                      <time>{formatDate(item.due_date)}</time>
                    </footer>
                    {owner && (
                      <select
                        aria-label={`Estado de ${item.title}`}
                        value={item.status}
                        onChange={(event) =>
                          void moveWorkItem(
                            item.id,
                            event.target.value as WorkItemStatus,
                          )
                        }
                      >
                        {boardStatuses.map((option) => (
                          <option value={option} key={option}>
                            {workItemStatusLabel(
                              option,
                              data.organization.name,
                            )}
                          </option>
                        ))}
                      </select>
                    )}
                  </article>
                ))}
                {items.length === 0 && (
                  <div className="column-empty">Sin pendientes</div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </Surface>
  );
}

function MeetingCard({
  meeting,
  onEdit,
  owner,
}: {
  meeting?: Meeting;
  onEdit(): void;
  owner: boolean;
}) {
  return (
    <Surface
      title="Próxima reunión"
      icon={<CalendarDays size={18} />}
      className="meeting-card dashboard-card"
    >
      <div className="meeting-date">
        <span>
          <CalendarDays />
        </span>
        <div>
          <strong>
            {meeting?.starts_at
              ? formatLongDate(meeting.starts_at)
              : "Fecha por confirmar"}
          </strong>
          <small>
            {meeting?.starts_at
              ? new Intl.DateTimeFormat("es-MX", {
                  hour: "numeric",
                  minute: "2-digit",
                }).format(new Date(meeting.starts_at))
              : "Fecha y hora por confirmar"}
          </small>
        </div>
      </div>
      <h3>{meeting?.title ?? "Sin reunión programada"}</h3>
      <p>{meeting?.objective}</p>
      {meeting?.meeting_url ? (
        <a
          className="button primary wide"
          href={meeting.meeting_url}
          target="_blank"
          rel="noreferrer"
        >
          <Video size={18} />
          Entrar a Google Meet
        </a>
      ) : owner ? (
        <button className="button primary wide" onClick={onEdit}>
          <Plus size={18} />
          Programar reunión
        </button>
      ) : (
        <p className="muted-copy">El enlace se publicará cuando la reunión quede programada.</p>
      )}
      {owner && <button className="text-button centered" onClick={onEdit}>Crear o editar reunión</button>}
    </Surface>
  );
}

function CollaborationCards({
  onChange,
}: {
  onChange(item?: ChangeRequest): void;
}) {
  const { data, addChangeRequestComment, uploadFile, downloadFile } = useWorkspace();
  if (!data) return null;
  return (
    <section className="collaboration-grid">
      <Surface
        title="Cambios solicitados"
        icon={<FileQuestion size={18} />}
        action={{ label: "Nuevo cambio", onClick: () => onChange() }}
        className="collaboration-card"
      >
        <div className="collaboration-scroll">
          {[...data.changes]
            .filter((change) => !change.archived_at)
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .map((change) => (
              <article key={change.id}>
                <header>
                  <b>{change.title}</b>
                  <StatusPill tone="blue">
                    {changeLabels[change.status] ?? change.status}
                  </StatusPill>
                </header>
                <p>{change.description}</p>
                <footer>
                  <span>{change.requester_name ?? "Usuario"}</span>
                  <time>{formatDate(change.created_at)}</time>
                </footer>
                <div className="change-comments">{data.changeComments.filter((comment) => comment.change_request_id === change.id).map((comment) => <p key={comment.id}><strong>{comment.author_name}</strong> · <time>{formatDate(comment.created_at)}</time><br/>{comment.body}</p>)}</div>
                {data.files.filter((file) => file.change_request_id === change.id).map((file) => <button className="text-button" key={file.id} onClick={() => void downloadFile(file.storage_path, file.name)}>{file.name}</button>)}
                <div className="inline-actions"><button className="text-button" onClick={() => { const body = window.prompt("Comentario para este cambio"); if (body?.trim()) void addChangeRequestComment(change.id, body); }}>Comentar</button><label className="text-button file-label">Adjuntar<input hidden type="file" onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadFile(file, "Otro", `Adjunto de ${change.title}`, change.id); }}/></label></div>
              </article>
            ))}
        </div>
      </Surface>
      <Surface
        title="Decisiones"
        icon={<Scale size={18} />}
        className="collaboration-card"
      >
        <div className="collaboration-scroll">
          {[...data.decisions]
            .filter((decision) => !decision.archived_at)
            .sort((a, b) => b.decided_at.localeCompare(a.decided_at))
            .map((decision) => (
              <article key={decision.id}>
                <header>
                  <b>{decision.text}</b>
                  <StatusPill
                    tone={decision.status === "REJECTED" ? "gray" : "green"}
                  >
                    {decisionLabels[decision.status] ??
                      decision.status ??
                      "Aprobado"}
                  </StatusPill>
                </header>
                <p>{decision.context}</p>
                {decision.change_request_id && (
                  <small>
                    Relacionado con CR-
                    {decision.change_request_id.slice(0, 8).toUpperCase()}
                  </small>
                )}
                <footer>
                  <time>{formatDate(decision.decided_at)}</time>
                </footer>
              </article>
            ))}
        </div>
      </Surface>
    </section>
  );
}

function CommercialWidget() {
  const { data } = useWorkspace();
  const navigate = useNavigate();
  if (!data || data.viewer.role === "CLIENT_MEMBER") return null;
  const summary = calculateCommercialSummary(
    data.quotes,
    data.quoteVersions,
    data.quoteItems,
    data.payments,
  );
  return (
    <Surface
      title="Cotizaciones"
      icon={<ReceiptText size={18} />}
      className="dashboard-card"
    >
      <div className="commercial-totals dashboard-commercial-totals">
        {data.viewer.role === "POSITRONICO_OWNER" && (
          <span>
            Borradores <b>{summary.drafts}</b>
          </span>
        )}
        <span>
          Pendiente aprobación <b>{formatMoney(summary.pending)}</b>
        </span>
        <span>
          Aprobado <b>{formatMoney(summary.approved)}</b>
        </span>
        <span>
          Cobrado <b>{formatMoney(summary.paid)}</b>
        </span>
        <span>
          Por cobrar <b>{formatMoney(summary.outstanding)}</b>
        </span>
      </div>
      <button
        className="button subtle wide"
        onClick={() => navigate("/cotizaciones")}
      >
        Abrir cotizaciones
      </button>
    </Surface>
  );
}

export function DashboardPage() {
  const {
    data,
    loading,
    error,
    save,
    addWorkItemComment,
    addChangeRequestComment,
    completeWaitingClient,
  } = useWorkspace();
  const navigate = useNavigate();
  const [item, setItem] = useState<WorkItem | null>(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [change, setChange] = useState<ChangeRequest | null | undefined>(
    undefined,
  );
  const [decision, setDecision] = useState<Decision | null | undefined>(
    undefined,
  );
  if (loading)
    return (
      <div className="page-loading">
        <span />
        <p>Cargando el proyecto…</p>
      </div>
    );
  if (error || !data)
    return (
      <div className="page-error">
        <h1>No pudimos abrir el proyecto</h1>
        <p>{error ?? "Revisa la configuración de Supabase."}</p>
      </div>
    );
  const owner = data.viewer.role === "POSITRONICO_OWNER";
  const meeting = nextMeeting(data.meetings);
  const recentFiles = data.files.slice(0, 6);
  const primaryAgent = data.agents[0];
  const waitingClient = data.workItems.filter(
    (candidate) =>
      candidate.status === "WAITING_CLIENT" && !candidate.archived_at,
  );
  const submitItem = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!item || !owner) return;
    const values = new FormData(event.currentTarget);
    await save("work_items", {
      id: item.id,
      title: String(values.get("title")),
      description: String(values.get("description")),
      due_date: String(values.get("due_date")) || null,
      status: String(values.get("status")),
      priority: String(values.get("priority")),
      assignee_id: String(values.get("assignee_id")) || null,
    });
    const comment = String(values.get("comment") ?? "");
    if (comment.trim()) await addWorkItemComment(item.id, comment);
    setItem(null);
  };
  const submitMeeting = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    await save("meetings", {
      id: meeting?.id,
      title: String(values.get("title")),
      objective: String(values.get("objective")),
      starts_at: String(values.get("starts_at")) || null,
      timezone: String(values.get("timezone")),
      meeting_url: String(values.get("meeting_url")) || null,
      status: String(values.get("status")),
      agenda: meeting?.agenda ?? "",
      notes: meeting?.notes ?? "",
    });
    setMeetingOpen(false);
  };
  const submitChange = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const changeId = change?.id;
    const saved = await save("change_requests", {
      id: changeId,
      title: String(values.get("title")),
      description: String(values.get("description")),
      category: String(values.get("category")),
      priority: String(values.get("priority")),
      status: "NEW",
    });
    const comment = String(values.get("comment") ?? "").trim();
    if (saved && changeId && comment)
      await addChangeRequestComment(changeId, comment);
    setChange(undefined);
  };
  const submitDecision = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    await save("decisions", {
      id: decision?.id,
      text: String(values.get("text")),
      context: String(values.get("context")),
      status: String(values.get("status")),
      change_request_id: String(values.get("change_request_id")) || null,
      resolution_note: String(values.get("resolution_note")),
      decided_at: String(values.get("decided_at")),
    });
    setDecision(undefined);
  };
  return (
    <div className="dashboard page-enter">
      <DashboardPrimaryGrid>
        <ProjectStatus />
        <Surface
          title="Calendario del proyecto"
          icon={<CalendarDays size={18} />}
          className="calendar-card dashboard-card"
        >
          <CalendarWidget />
        </Surface>
        <MeetingCard meeting={meeting} owner={owner} onEdit={() => setMeetingOpen(true)} />
        <DashboardNeedsCard
          organizationName={data.organization.name}
          items={waitingClient}
          onComplete={completeWaitingClient}
          onViewAll={() =>
            document
              .querySelector(".needs-card")
              ?.scrollIntoView({ behavior: "smooth" })
          }
        />
      </DashboardPrimaryGrid>
      <WorkBoard
        onItem={(selected) => (owner ? setItem(selected) : undefined)}
      />
      <CollaborationCards
        onChange={(selected) => setChange(selected ?? null)}
      />
      <div className="dashboard-secondary-grid dashboard-files-grid">
        <Surface
          title="Archivos"
          icon={<FileText size={18} />}
          action={{ label: "Abrir archivos", onClick: () => navigate("/archivos") }}
          className={`dashboard-card ${primaryAgent ? "" : "secondary-wide"}`}
        >
          <div className="recent-files">
            {recentFiles.length ? (
              recentFiles.map((file) => (
                <button key={file.id} onClick={() => navigate("/archivos")}>
                  <span
                    className={`file-icon ${file.mime_type.includes("pdf") ? "pdf" : "doc"}`}
                  >
                    <File />
                  </span>
                  <span>
                    <strong>{file.name}</strong>
                    <small>
                      {formatBytes(file.size_bytes)} ·{" "}
                      {formatDate(file.created_at)}
                    </small>
                  </span>
                  <MoreVertical />
                </button>
              ))
            ) : (
              <p className="muted-copy">Aún no hay archivos.</p>
            )}
          </div>
        </Surface>
        {primaryAgent && (
          <Surface
            title="Agente"
            icon={<Sparkles size={18} />}
            className="dashboard-card"
          >
            <div className="agent-widget">
              <span className="agent-orb">
                <Bot />
              </span>
              <div>
                <strong>{primaryAgent.name}</strong>
                <StatusPill tone="amber">{primaryAgent.status}</StatusPill>
                <p>Módulo independiente en construcción.</p>
              </div>
              <button
                className="button subtle"
                onClick={() => navigate(`/agentes/${primaryAgent.id}`)}
              >
                Abrir agente
              </button>
            </div>
          </Surface>
        )}
      </div>
      {data.viewer.role !== "CLIENT_MEMBER" && (
        <div
          className={`dashboard-summary-grid ${owner ? "owner" : "admin"}`}
        >
          {owner && (
            <Surface
              title="Actividad relevante"
              icon={<Clock3 size={18} />}
              className="activity-card dashboard-card"
            >
              <div className="activity-list">
                {[...data.activities]
                  .sort((left, right) =>
                    right.created_at.localeCompare(left.created_at),
                  )
                  .map((activity, index) => (
                    <div key={activity.id}>
                      <span className={`activity-symbol tone-${index % 4}`}>
                        {index % 2 ? <CheckCircle2 /> : <MessageCircle />}
                      </span>
                      <p>
                        <strong>{activity.actor_name}</strong>{" "}
                        {activity.description}
                      </p>
                      <time>{formatDate(activity.created_at)}</time>
                    </div>
                  ))}
              </div>
            </Surface>
          )}
          <CommercialWidget />
        </div>
      )}
      <Modal
        open={Boolean(item)}
        title="Editar pendiente interno"
        onClose={() => setItem(null)}
      >
        {item && owner && (
          <form className="form-grid" onSubmit={submitItem}>
            <label className="full">
              Título
              <input name="title" defaultValue={item.title} required />
            </label>
            <label className="full">
              Descripción
              <textarea name="description" defaultValue={item.description} />
            </label>
            <label>
              Estado
              <select name="status" defaultValue={item.status}>
                {boardStatuses.map((status) => (
                  <option value={status} key={status}>
                    {workItemStatusLabel(status, data.organization.name)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Prioridad
              <select name="priority" defaultValue={item.priority}>
                {(
                  ["LOW", "MEDIUM", "HIGH", "URGENT"] as WorkItemPriority[]
                ).map((priority) => (
                  <option value={priority} key={priority}>
                    {priorityLabel(priority)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Fecha objetivo
              <input
                type="date"
                name="due_date"
                defaultValue={item.due_date ?? ""}
              />
            </label>
            <label>
              Responsable
              <select name="assignee_id" defaultValue={item.assignee_id ?? ""}>
                <option value="">Sin asignar</option>
                {data.members
                  .filter(
                    (member) => member.status === "ACTIVE" && member.user_id,
                  )
                  .map((member) => (
                    <option value={member.user_id!} key={member.id}>
                      {member.full_name}
                    </option>
                  ))}
              </select>
            </label>
            <label className="full">
              Añadir comentario
              <textarea name="comment" />
            </label>
            <FormActions onCancel={() => setItem(null)} />
          </form>
        )}
      </Modal>
      <Modal
        open={meetingOpen}
        title={meeting ? "Editar reunión" : "Programar reunión"}
        onClose={() => setMeetingOpen(false)}
      >
        <form className="form-grid" onSubmit={submitMeeting}>
          <label className="full">
            Título
            <input name="title" defaultValue={meeting?.title ?? ""} required />
          </label>
          <label className="full">
            Objetivo
            <textarea
              name="objective"
              defaultValue={meeting?.objective ?? ""}
            />
          </label>
          <label>
            Fecha y hora
            <input
              type="datetime-local"
              name="starts_at"
              defaultValue={meeting?.starts_at?.slice(0, 16) ?? ""}
            />
          </label>
          <label>
            Zona horaria
            <input
              name="timezone"
              defaultValue={meeting?.timezone ?? "America/Mexico_City"}
            />
          </label>
          <label className="full">
            Google Meet URL
            <input
              name="meeting_url"
              type="url"
              pattern="https://meet\.google\.com/.+"
              defaultValue={meeting?.meeting_url ?? ""}
            />
          </label>
          <label>
            Estado
            <select
              name="status"
              defaultValue={meeting?.status ?? "Programada"}
            >
              <option>Por confirmar</option>
              <option>Programada</option>
              <option>Completada</option>
              <option value="CANCELLED">Cancelada</option>
            </select>
          </label>
          <FormActions onCancel={() => setMeetingOpen(false)} />
        </form>
      </Modal>
      <Modal
        open={change !== undefined}
        title={change ? "Editar cambio" : "Nuevo cambio solicitado"}
        onClose={() => setChange(undefined)}
      >
        <form className="form-grid" onSubmit={submitChange}>
          <label className="full">
            Título
            <input name="title" defaultValue={change?.title ?? ""} required />
          </label>
          <label className="full">
            Descripción
            <textarea
              name="description"
              defaultValue={change?.description ?? ""}
              required
            />
          </label>
          <label>
            Categoría
            <input
              name="category"
              defaultValue={change?.category ?? "Alcance"}
              required
            />
          </label>
          <label>
            Prioridad
            <select name="priority" defaultValue={change?.priority ?? "MEDIUM"}>
              <option>LOW</option>
              <option>MEDIUM</option>
              <option>HIGH</option>
              <option>URGENT</option>
            </select>
          </label>
          {change?.id && (
            <label className="full">
              Añadir comentario
              <textarea
                name="comment"
                placeholder="Contexto, avance o respuesta…"
              />
            </label>
          )}
          <FormActions onCancel={() => setChange(undefined)} />
        </form>
      </Modal>
      <Modal
        open={decision !== undefined}
        title={decision ? "Editar decisión" : "Nueva decisión"}
        onClose={() => setDecision(undefined)}
      >
        {data.viewer.role !== "CLIENT_MEMBER" && (
          <form className="form-grid" onSubmit={submitDecision}>
            <label className="full">
              Decisión
              <input name="text" defaultValue={decision?.text ?? ""} required />
            </label>
            <label className="full">
              Contexto
              <textarea name="context" defaultValue={decision?.context ?? ""} />
            </label>
            <label>
              Estado
              <select
                name="status"
                defaultValue={decision?.status ?? "APPROVED"}
              >
                {Object.entries(decisionLabels).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Fecha
              <input
                type="date"
                name="decided_at"
                defaultValue={
                  decision?.decided_at ?? new Date().toISOString().slice(0, 10)
                }
              />
            </label>
            <label className="full">
              Cambio relacionado
              <select
                name="change_request_id"
                defaultValue={decision?.change_request_id ?? ""}
              >
                <option value="">Sin relación</option>
                {data.changes.map((changeItem) => (
                  <option value={changeItem.id} key={changeItem.id}>
                    CR-{changeItem.id.slice(0, 8).toUpperCase()} ·{" "}
                    {changeItem.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="full">
              Nota de resolución
              <textarea
                name="resolution_note"
                defaultValue={decision?.resolution_note ?? ""}
              />
            </label>
            <FormActions onCancel={() => setDecision(undefined)} />
          </form>
        )}
      </Modal>
    </div>
  );
}
